import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Sin acentos ni mayúsculas, para que "desa" encuentre "Desayuno" y "automoviles" encuentre "automóviles".
export const normalizar = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const MAX_VISIBLES = 50;
const ALTO_LISTA = 240;

/*
 * Campo único de "escribe y elige": mientras se escribe, la lista flota pegada al campo y se acota,
 * sin empujar el resto de la pantalla (como un select, pero sin la ventana nativa).
 * Reemplaza al <select> en catálogos largos (actividad, estado, colonia…).
 *
 * La lista va en `position: fixed` calculada desde el campo: no la recorta el scroll del panel y,
 * si abajo no cabe, se abre hacia arriba.
 *
 * props:
 *   options    [{ value, label }] opciones filtrables (se busca en label y en value)
 *   fijas      [{ value, label }] opciones que siempre salen al final (p. ej. "Otra…"); no se filtran
 *   value      valor elegido (el texto del campo muestra la etiqueta de la opción que coincide)
 *   onChange   recibe el valor de la opción elegida
 *   ariaLabel  nombre accesible del campo
 *   libre      true = "elige o escribe otro": lo que se escribe ES el valor y la lista solo sugiere
 *   placeholder, disabled, invalid, className
 */
export const BuroCombobox = ({ options, fijas = [], value, onChange, ariaLabel, libre = false, placeholder = 'Escribe para buscar…', disabled = false, invalid = false, className = 'bu-input' }) => {
  const [texto, setTexto] = useState(null);   // null = no se está escribiendo: se ve la etiqueta elegida
  const [abierto, setAbierto] = useState(false);
  // En modo libre no hay opción activa hasta usar las flechas: Enter no debe pisar lo escrito.
  const inicial = libre ? -1 : 0;
  const [activo, setActivo] = useState(inicial);
  const [pos, setPos] = useState(null);
  const campo = useRef(null);
  const listaId = useRef(`bu-combo-${Math.random().toString(36).slice(2, 8)}`).current;

  const elegida = [...fijas, ...options].find((o) => String(o.value) === String(value ?? ''));
  const mostrado = libre ? String(value ?? '') : (texto !== null ? texto : (elegida ? elegida.label : String(value ?? '')));
  // En modo libre se filtra con lo escrito (que ya es el valor); si no, con el texto de búsqueda.
  const consulta = libre ? (texto !== null ? String(value ?? '') : '') : texto;

  const { visibles, total } = useMemo(() => {
    const palabras = normalizar(consulta ?? '').split(/\s+/).filter(Boolean);
    const coincide = (o) => palabras.every((p) => normalizar(`${o.value} ${o.label}`).includes(p));
    const todas = palabras.length ? options.filter(coincide) : options;
    return { visibles: [...todas.slice(0, MAX_VISIBLES), ...fijas], total: todas.length };
  }, [options, fijas, consulta]);

  // Posición de la lista según el campo: debajo si cabe; si no, hacia arriba.
  const colocar = useCallback(() => {
    if (!campo.current) return;
    const r = campo.current.getBoundingClientRect();
    const abajo = window.innerHeight - r.bottom - 12;
    const arriba = r.top - 12;
    const bajo = abajo >= Math.min(ALTO_LISTA, 160) || abajo >= arriba;
    setPos(bajo
      ? { left: r.left, width: r.width, top: r.bottom + 4, maxHeight: Math.max(80, Math.min(ALTO_LISTA, abajo)) }
      : { left: r.left, width: r.width, bottom: window.innerHeight - r.top + 4, maxHeight: Math.max(80, Math.min(ALTO_LISTA, arriba)) });
  }, []);

  useEffect(() => {
    if (!abierto) return undefined;
    colocar();
    window.addEventListener('resize', colocar);
    document.addEventListener('scroll', colocar, true);
    return () => { window.removeEventListener('resize', colocar); document.removeEventListener('scroll', colocar, true); };
  }, [abierto, colocar]);

  const cerrar = () => { setAbierto(false); setTexto(null); setActivo(inicial); };
  const elegir = (o) => { onChange(o.value); cerrar(); };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setAbierto(true); setActivo((a) => Math.min(a + 1, visibles.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActivo((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && abierto && visibles[activo]) { e.preventDefault(); elegir(visibles[activo]); }
    else if (e.key === 'Escape' || e.key === 'Tab') cerrar();
  };

  return (
    <div className="bu-combo">
      <input
        ref={campo}
        className={className}
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={abierto}
        aria-controls={listaId}
        aria-autocomplete="list"
        aria-invalid={invalid ? 'true' : undefined}
        autoComplete="off"
        placeholder={placeholder}
        disabled={disabled}
        value={mostrado}
        onFocus={(e) => e.target.select()}
        onClick={() => setAbierto(true)}
        onBlur={cerrar}
        onChange={(e) => {
          setTexto(e.target.value); setAbierto(true); setActivo(inicial);
          if (libre) onChange(e.target.value);
        }}
        onKeyDown={onKeyDown}
      />
      {abierto && !disabled && (
        <ul id={listaId} role="listbox" className="bu-combo-list" style={pos || { visibility: 'hidden' }}>
          {visibles.length === 0 && <li className="bu-combo-vacio">Sin resultados</li>}
          {visibles.map((o, i) => (
            <li
              key={`${o.value}-${i}`}
              role="option"
              aria-selected={String(o.value) === String(value ?? '')}
              className={`bu-combo-opt${i === activo ? ' activo' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); elegir(o); }}
              onMouseEnter={() => setActivo(i)}
            >
              {o.label}
            </li>
          ))}
          {total > MAX_VISIBLES && <li className="bu-combo-vacio">Mostrando {MAX_VISIBLES} de {total}: sigue escribiendo para acotar.</li>}
        </ul>
      )}
    </div>
  );
};
